import * as d3 from 'd3'
import draw from '@/views/Scenario/draw/draw'
import { NodeType } from '@/utils/const'
import AppService from '@/services/app.service'
import $filters from '@/modules/fptai/filters/filters'

const characterWidth = 8

function limitLength (str, max) {
    max = Math.floor(max)
    if (str.length > max) {
        return str.substring(0, max - 3) + '...'
    }
    return str
}

function createNode (dataBus, svg, nodeList) {
    let {
        svgPadding,
        cellWidth,
        cellHeight,
        duration,
        circleRadius,
        rectWidth,
        rectHeight,
        toggleRadius,
        reuseWidth,
        reuseHeight
    } = dataBus.graphConfig
    // create group node
    let gNode = svg.select('.g-node')
    if (gNode.empty()) {
        gNode = svg.append('g').attr('class', 'g-node')
            .attr('transform', `translate(${svgPadding},${svgPadding})`)
    }
    let gCellSelection = gNode.selectAll('.g-cell').data(nodeList, d => d.id)
    // create new node
    let gCellEnter = gCellSelection.enter()
        .append('g')
        .attr('id', d => `gCell-${d.id}`)
        .attr('data-node', d => d.data.id)
        .attr('data-root', d => d.isPortal ? 'portal' : d.data.id)
        .attr('class', d => 'g-cell ' + d.type.toLowerCase())
        .classed('portal', d => d.isPortal)
        .classed('transitioning', true)
        .attr('transform', d => {
            if (dataBus.openNode) {
                return `translate(${dataBus.openNode.x * cellWidth}, ${dataBus.openNode.y * cellHeight})`
            } else {
                return `translate(${-cellWidth}, ${-cellHeight})`
            }
        })
        .attr('opacity', dataBus.openNode ? 0 : 1)
    gCellEnter
        .on('click', (e, d) => {
            dataBus.onNodeClick(d)
        })
        .on('contextmenu', (e, d) => {
            if (d.type === NodeType.STEP) {
                e.preventDefault()
                dataBus.onContextMenu(e, d)
            }
        })
        .on('mouseenter', (e, d) => {
            let node = d
            let parent = node.parent
            while (parent) {
                d3.select(`#link-${parent.id}-${node.id}`).classed('active', true)
                node = parent
                parent = node.parent
            }
        })
        .on('mouseleave', (e, d) => {
            let node = d
            let parent = node.parent
            while (parent) {
                d3.select(`#link-${parent.id}-${node.id}`).classed('active', false)
                node = parent
                parent = node.parent
            }
        })
    let gCellAll = gCellEnter.merge(gCellSelection)
    gCellAll
        .classed('active', d =>
            d.id === dataBus.activeGraphStepNodeId ||
            d.id === dataBus.activeGraphCardNodeId
        )
        .transition()
        .duration(duration)
        .attr('transform', d => `translate(${d.x * cellWidth}, ${d.y * cellHeight})`)
        .attr('opacity', 1)
        .end()
        .catch(() => {
        }) // prevent d3 logging error
        .finally(() => gCellAll.classed('transitioning', false))
    // create STEP (square)
    gCellEnter
        .filter(d => d.type === 'STEP')
        .append('rect')
        .attr('class', 'step-box')
        .attr('rx', 5)
        .attr('ry', 5)
        .attr('x', (cellWidth - rectWidth) / 2)
        .attr('y', (cellHeight - rectHeight) / 2)
        .attr('width', rectWidth)
        .attr('height', rectHeight)
    // create CARD (circle)
    gCellEnter
        .filter(d => d.type === 'CARD')
        .append('circle')
        .attr('cx', cellWidth / 2)
        .attr('cy', cellHeight / 2)
        .attr('r', circleRadius)
    gCellEnter
        .filter(d => d.type === 'CARD')
        .append('image')
        .attr('href', d => `/img/scenario/card/normal/${d.data.data.card_type_id}.svg`)
        .attr('x', cellWidth / 2 - 8)
        .attr('y', cellHeight / 2 - 8)
        .attr('height', 16)
    // CARD condition
    gCellEnter
        .filter(d => d.type === 'CARD')
        .append('image')
        .attr('class', 'condition-indicator')
        .attr('href', '/img/scenario/condition.svg')
        .attr('x', cellWidth / 2 - circleRadius / 2 - 1)
        .attr('y', cellHeight / 2 + circleRadius - 3)
        .attr('height', circleRadius + 2)
    gCellAll
        .filter(d => d.type === 'CARD')
        .classed('has-condition', d => d.data.hasCondition)
    // CARD active icon color
    gCellAll
        .filter(d => d.type === 'CARD')
        .select('image')
        .attr('href', d => {
            let folder = d.data.id === dataBus.activeCardCode ? 'white' : 'normal'
            return `/img/scenario/card/${folder}/${d.data.data.card_type_id}.svg`
        })
    // disabled
    gCellAll
        .classed('disabled', d => !d.data.data.activate)
    // name
    gCellEnter
        .filter(d => d.type === 'STEP')
        .append('text')
        .attr('class', 'name')
        .attr('dx', 10)
        .attr('dy', cellHeight / 2 - rectHeight / 4)
        .attr('alignment-baseline', 'middle')
    // set text value using gCellAll to work in case step rename
    gCellAll
        .filter(d => d.type === 'STEP')
        .select('text')
        .text(d => limitLength(d.name, (rectWidth - 20) / characterWidth))
    // intent
    gCellEnter
        .filter(d => d.type === 'STEP')
        .append('rect')
        .attr('rx', 5)
        .attr('ry', 5)
        .attr('x', (cellWidth - rectWidth) / 2 + 1)
        .attr('y', (cellHeight - rectHeight) / 2 + rectHeight / 2)
        .attr('width', rectWidth - 2)
        .attr('height', rectHeight / 2 - 1)
        .attr('class', 'intent-box')
    gCellEnter
        .filter(d => d.type === 'STEP')
        .append('text')
        .attr('class', 'intent')
        .attr('dx', 10)
        .attr('dy', cellHeight / 2 + rectHeight / 4)
        .attr('alignment-baseline', 'middle')
        .text('')
    gCellAll
        .select('text.intent')
        .text(d => {
            if (d.data.intentList.length < 1) {
                return ''
            }
            if (d.data.intentList.length > 1) {
                let name = limitLength(d.data.intentList[0], (rectWidth - 5) / characterWidth - 3)
                return `${name} (+${d.data.intentList.length - 1})`
            }
            return limitLength(d.data.intentList[0], (rectWidth - 20) / characterWidth)
        })
    // toggle children
    gCellEnter
        .filter(d => d.type === 'STEP')
        .append('image')
        .attr('class', 'toggle-children')
        .attr('href', '/img/scenario/open.svg')
        .attr('x', (cellWidth + rectWidth) / 2 - toggleRadius)
        .attr('y', cellHeight / 2 - toggleRadius)
        .attr('width', toggleRadius * 2)
        .attr('height', toggleRadius * 2)
    gCellAll
        .filter(d => d.type === 'STEP')
        .classed('has-children', d => d.data.children.length > 0)
        .select('image.toggle-children')
        .attr('width', d => d.data.children.length > 0 ? toggleRadius * 2 : 0)
        .attr('height', d => d.data.children.length > 0 ? toggleRadius * 2 : 0)
    gCellAll
        .select('image.toggle-children')
        .on('mousedown', e => {
            e.stopPropagation()
        })
        .on('click', (e, d) => {
            e.stopPropagation()
            let node = d.data
            let parent = d.parent
            while (parent) {
                if (parent.data === node) {
                    AppService.inform($filters.tc('ancestorOfItOwn'))
                    return
                }
                parent = parent.parent
            }
            for (let nodeOut of nodeList) {
                if (nodeOut.data === d.data) {
                    if (dataBus.nodeOpenMap[nodeOut.id]) {
                        dataBus.closeNode = nodeOut
                    }
                    if (nodeOut.id !== d.id) {
                        dataBus.nodeOpenMap[nodeOut.id] = false
                    }
                }
            }
            dataBus.nodeOpenMap[d.id] = !dataBus.nodeOpenMap[d.id]
            if (dataBus.nodeOpenMap[d.id]) {
                dataBus.openNode = d
            } else {
                dataBus.closeNode = d
            }
            // disable transition to prevent unexpected moving node
            svg.style('transition', 'none')
            draw(dataBus)
            setTimeout(() => {
                svg.style('transition', 'all 0.3s ease')
            }, 0.31)
        })
    // tooltips
    gCellEnter.append('title')
        .text(d => d.name)
    // reuse indicator
    let countGroup = gCellEnter
        .filter(d => d.type === 'STEP')
        .append('g')
        .attr('class', 'reuse-box')
        .attr(
            'transform',
            `translate(${(cellWidth + rectWidth - reuseWidth) / 2}, ${(cellHeight - rectHeight - reuseHeight) / 2})`
        )
    countGroup
        .append('rect')
        .attr('class', 'portal-count')
        .attr('rx', reuseHeight / 2)
        .attr('ry', reuseHeight / 2)
        .attr('width', reuseWidth)
        .attr('height', reuseHeight)
    countGroup
        .append('image')
        .attr('href', '/img/scenario/reuse.svg')
        .attr('x', 1)
        .attr('y', 1)
        .attr('width', reuseHeight - 2)
        .attr('height', reuseHeight - 2)
    countGroup
        .append('text')
        .attr('class', 'portal-count')
        .attr('alignment-baseline', 'middle')
        .attr('dx', reuseHeight)
        .attr('dy', reuseHeight / 2 + 1)
    countGroup
        .append('title')
    gCellAll
        .filter(d => d.type === 'STEP')
        .classed('has-reuse', d => dataBus.data.countToThisStepMap[d.data.id] > 0)
    gCellAll
        .select('.reuse-box text')
        .text(d => dataBus.data.countToThisStepMap[d.data.id])
    gCellAll
        .select('.reuse-box title')
        .text(d => {
            return dataBus.data.allStepToThisStepMap[d.data.id].map(item => item.name).join('\n')
        })
    // remove node
    gCellSelection.exit()
        .transition()
        .duration(duration)
        .attr('transform', () => {
            let closeNode = dataBus.closeNode || {
                x: -1,
                y: -1
            }
            return `translate(${closeNode.x * cellWidth}, ${closeNode.y * cellHeight})`
        })
        .attr('opacity', 0)
        .remove()
}

export default createNode
